import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import type { DataDefField } from '@safetyhub/shared';
import { adminApi } from '../../lib/api';
import { ModalCancelButton } from '../../components/ModalCancelButton';
import type { DatasetXlsxSheet } from '../../lib/api';
import { DefaultValueControl, ImportPreviewStep, mappedColumnCount } from './datasetImportShared';
import {
  autoMatchColumns,
  buildComposeColumnDrafts,
  buildHeaderLabels,
  buildRowsFromMapping,
  detectExclusivePairs,
  draftsToFields,
  draftsToMapping,
  isPairRef,
  MAX_HEADER_ROW_COUNT,
  mergedPairSecondCols,
  suggestHeaderRowCount,
  suggestHeaderRowIndex,
  togglePairMerge,
  type BuildRowsResult,
  type ColumnDefaults,
  type ColumnMapping,
  type ColumnSource,
  type ComposeColumnDraft,
  type ExclusivePairCandidate,
  type InferredWidget,
} from './datasetExcelImport';

/**
 * 직접 입력 데이터셋(manual dataDef)에 엑셀 행을 가져오는 모달.
 *
 * 줄기: docs/work-streams/done/dataset-excel-row-import.md (P2)
 *
 * **두 모드가 같은 흐름을 공유한다** — 파일 → 시트·헤더 행 → ③칼럼 → 미리보기 → 반영.
 * 다른 것은 ③단계뿐이다:
 * - `map`(기존 카드 `⬆ 엑셀에서 행 추가`): 칼럼 **불변**. key는 셀 바인딩·작성폼 파생·참조
 *   모델이 붙잡고 있는 계약이라 여기서 손대지 않는다. 데이터셋 칼럼 ← 엑셀 칼럼을 고르고,
 *   엑셀에 없는 칼럼은 **칼럼별 기본값**으로 채운다.
 * - `compose`(＋데이터 추가 → 엑셀에서 시작): 엑셀 헤더가 곧 칼럼 후보. 가져올 칼럼을 고르고
 *   라벨·위젯을 확인한다. 생성 전이라 key 발급이 자유롭다(참조 파급 0).
 *
 * 파싱은 서버(`parse-xlsx`)가 원시 격자만 돌려주는 동기 1콜이다(AI 없음 → Job 폴링 없음).
 * 값 변환·자동 매칭·위젯 추론은 전부 순수 유틸(datasetExcelImport.ts)이 맡고 여기서는
 * 그 결과를 그리기만 한다.
 */

export type DatasetExcelImportMode = 'map' | 'compose';

/** 반영 결과 — 호출부가 데이터셋을 어떻게 갱신할지는 모드로 갈린다. */
export type DatasetExcelImportResult =
  | { mode: 'map'; rows: Record<string, unknown>[]; replace: boolean }
  | { mode: 'compose'; fields: DataDefField[]; rows: Record<string, unknown>[] };

interface DatasetExcelImportModalCommonProps {
  open: boolean;
  onClose: () => void;
  onApply: (result: DatasetExcelImportResult) => void;
}

interface DatasetExcelImportMapProps extends DatasetExcelImportModalCommonProps {
  mode: 'map';
  /** 대상 데이터셋의 칼럼(불변) — 여기로 넣을 엑셀 칸만 고른다. */
  fields: readonly DataDefField[];
  /** 기존 기본행 수 — 추가/교체 선택 문구에 쓴다. */
  existingRowCount: number;
  /** 대상 데이터셋 표시명(제목 보조 문구). */
  datasetLabel?: string;
}

interface DatasetExcelImportComposeProps extends DatasetExcelImportModalCommonProps {
  mode: 'compose';
  /**
   * 새 칼럼 key 발급기 — EditorPage와 같은 정본(`nextAutoFieldKey`)을 주입한다. 폴백을 두지
   * 않는 이유: 개수 기반(`field{길이+1}`) 규칙은 삭제 후 재추가 시 기존 키와 충돌해 두 칼럼이
   * 같은 값을 미러하는 사고를 냈다(2026-08-08 교육일지).
   */
  nextFieldKey: (existingKeys: readonly string[]) => string;
}

/**
 * 두 모드가 요구하는 입력은 서로 겹치지 않는다 — 판별 유니온으로 못박아, 모드에 필요한 값이
 * 빠진 채로 열리는 조합을 tsc가 막는다(옵셔널 + 내부 폴백은 정본을 두 벌로 만든다).
 * 공통 속성은 각 갈래가 상속한다 — `공통 & (A | B)` 꼴은 판별 좁히기가 걸리지 않는다.
 */
type DatasetExcelImportModalProps = DatasetExcelImportMapProps | DatasetExcelImportComposeProps;

/** map 모드가 아닐 때의 칼럼 자리 — 참조가 고정이라 미리보기 memo를 흔들지 않는다. */
const NO_FIELDS: readonly DataDefField[] = [];

type Step = 'file' | 'sheet' | 'columns' | 'preview';

/**
 * 업로드 전 파일 크기 선검사 값 — 서버 `MAX_XLSX_BYTES`와 같은 5MB다(서버 상수를 admin이
 * import할 수 없어 여기 다시 적는다). 선검사가 없으면 multer의 영문 "File too large"가
 * 서버의 한국어 안내보다 먼저 걸려 그대로 노출된다.
 */
const MAX_XLSX_BYTES = 5 * 1024 * 1024;

const WIDGET_LABEL: Record<InferredWidget, string> = {
  text: '텍스트',
  number: '숫자',
  checkbox: '체크박스',
};

/** 엑셀 칼럼 표시명 — 합성 헤더 문구가 있으면 그것, 없으면 `N번째 칸`. */
function excelColumnLabel(headerLabels: readonly string[], index: number): string {
  const text = (headerLabels[index] ?? '').trim();
  return text.length > 0 ? text : `${index + 1}번째 칸`;
}

/**
 * 배타 쌍 합성 항목의 `<option>` value 인코딩 — select value는 문자열뿐이라 두 열 index를
 * 접두사와 함께 접는다. mapping 상태의 정본은 어디까지나 PairColumnRef 객체이고, 이 문자열은
 * select 표시·복원에만 쓴다.
 */
function pairOptionValue(colIndexes: readonly [number, number]): string {
  return `pair:${colIndexes[0]}:${colIndexes[1]}`;
}

/** mapping 값 → select value 문자열(미매핑 '', 열 index, 쌍 인코딩). */
function sourceToSelectValue(source: ColumnSource | undefined): string {
  if (source === null || source === undefined) return '';
  return isPairRef(source) ? pairOptionValue(source.pair) : String(source);
}

export function DatasetExcelImportModal(props: DatasetExcelImportModalProps) {
  // 판별 유니온은 구조분해로는 좁혀지지 않는다 — props를 통째 받고 모드별 값만 여기서 꺼낸다.
  const { open, mode, onClose, onApply } = props;
  const targetFields = props.mode === 'map' ? props.fields : NO_FIELDS;
  const existingRowCount = props.mode === 'map' ? props.existingRowCount : 0;
  const datasetLabel = props.mode === 'map' ? props.datasetLabel : undefined;

  // 상태 초기화는 **마운트가 곧 초기 상태**다 — 호출부가 조건부로 마운트하므로 열릴 때마다
  // 새 인스턴스가 뜬다. 상시 마운트 + `open` 리셋 effect는 재열기 첫 프레임에 직전 세션의
  // 단계·시트·매핑을 그대로 보여 주고, 닫힌 뒤에도 파싱 격자를 페이지 수명 내내 붙들었다.
  const [step, setStep] = useState<Step>('file');
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheets, setSheets] = useState<DatasetXlsxSheet[]>([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [headerRowCount, setHeaderRowCount] = useState(1);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [defaults, setDefaults] = useState<ColumnDefaults>({});
  /** map 모드 ③단계의 배타 쌍 목록 — 합성 select 항목·자동 매칭 근거(③ 진입 시 감지). */
  const [pairs, setPairs] = useState<ExclusivePairCandidate[]>([]);
  const [drafts, setDrafts] = useState<ComposeColumnDraft[]>([]);
  const [replace, setReplace] = useState(false);
  /** compose ③단계 초안을 만들 때의 칼럼 이름(엑셀 열 index → 이름) — 미저장 닫기 판정의 기준선. */
  const [composeLabelBaseline, setComposeLabelBaseline] = useState<Record<number, string>>({});

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** 지금 들고 있는 격자를 만들어 낸 File — 같은 파일이면 다시 올리지 않는다. */
  const parsedFileRef = useRef<File | null>(null);
  /** ③단계 초안(자동 매칭·칼럼 후보)을 만든 근거 — `시트:헤더줄:줄수`. 같으면 다시 만들지 않는다. */
  const columnsBasisRef = useRef<string | null>(null);

  const activeSheet: DatasetXlsxSheet | undefined = sheets[sheetIndex];
  const cells = activeSheet?.cells ?? [];
  // 칼럼 표시명·자동 매칭이 쓰는 헤더 — 다행 헤더면 위아래 문구를 합친 라벨이다.
  const headerLabels = useMemo(
    () => buildHeaderLabels(cells, headerRowIndex, headerRowCount),
    [cells, headerRowIndex, headerRowCount],
  );

  // ③단계에서 실제로 쓰는 칼럼·매핑 — 두 모드를 같은 미리보기 계산으로 수렴시킨다.
  const effectiveFields: DataDefField[] = useMemo(
    () => (mode === 'map' ? [...targetFields] : draftsToFields(drafts)),
    [mode, targetFields, drafts],
  );
  const effectiveMapping: ColumnMapping = useMemo(
    () => (mode === 'map' ? mapping : draftsToMapping(drafts)),
    [mode, mapping, drafts],
  );
  // 병합 중인 쌍의 둘째 열 — 목록에 그리지 않는다(병합 draft 하나가 두 열을 대표한다).
  const hiddenPairCols = useMemo(() => mergedPairSecondCols(drafts), [drafts]);

  // 반환 타입을 못박아 둔다 — 조기 반환의 빈 배열이 `never[]`로 굳으면 아래 미리보기의
  // `rows.map(...)`이 유니온 시그니처 충돌로 깨진다.
  //
  // 미리보기 단계에서만 계산한다 — 칼럼 단계는 매핑·라벨을 한 글자 칠 때마다 상태가 바뀌는데,
  // 그때마다 전 행을 변환하면 타이핑이 눈에 띄게 밀린다(선택지 칼럼 1000행 기준 키당 215ms).
  const built = useMemo<BuildRowsResult>(() => {
    if (step !== 'preview' || effectiveFields.length === 0) {
      return { rows: [], warnings: [], defaultWarnings: [], skippedEmptyRows: 0 };
    }
    return buildRowsFromMapping({
      fields: effectiveFields,
      cells,
      headerRowIndex,
      headerRowCount,
      mapping: effectiveMapping,
      defaults,
    });
  }, [step, effectiveFields, cells, headerRowIndex, headerRowCount, effectiveMapping, defaults]);

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    setError(null);
  };

  /**
   * 파일 단계 실패 — 안내와 함께 선택을 비운다.
   *
   * 선택을 남겨 두면 파일을 고쳐서 **같은 이름으로** 다시 고를 때 `<input type="file">`이
   * 값이 같다는 이유로 change를 내지 않아, 사용자가 다시 골랐는데도 아무 일이 없어 보인다.
   */
  const failFileStep = useCallback((message: string) => {
    setError(message);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleParse = useCallback(async () => {
    if (!file) return;
    // 이미 읽은 그 파일이면 다시 올리지 않는다 — 재파싱은 사용자가 고른 헤더 줄을 자동 추천으로
    // 되돌려 놓는다(파일 단계로 "이전" 갔다가 그대로 "다음"을 누르는 흐름에서 조용한 손실).
    if (file === parsedFileRef.current && sheets.length > 0) {
      setStep('sheet');
      return;
    }
    if (file.size > MAX_XLSX_BYTES) {
      // 서버도 같은 상한을 막지만, multer가 그 앞에서 영문 메시지로 끊어 안내가 도달하지 않는다.
      failFileStep('엑셀은 5MB까지 올릴 수 있습니다. 가져올 시트·범위만 남긴 파일로 다시 올려주세요.');
      return;
    }
    setParsing(true);
    setError(null);
    try {
      const res = await adminApi.parseDatasetXlsx(file);
      if (!res.success || !res.data) {
        failFileStep(res.error?.message ?? '엑셀을 읽지 못했습니다.');
        return;
      }
      const parsed = res.data.sheets;
      if (parsed.length === 0) {
        failFileStep('엑셀에서 시트를 찾지 못했습니다.');
        return;
      }
      // 내용이 있는 첫 시트를 기본 선택 — 표지 시트가 앞에 오는 파일이 흔하다.
      const firstWithRows = parsed.findIndex((s) => s.rowCount > 0);
      const initialSheet = firstWithRows >= 0 ? firstWithRows : 0;
      parsedFileRef.current = file;
      // 새 격자라 ③단계 초안의 근거도 무효 — 다음 진입에서 다시 계산하게 둔다.
      columnsBasisRef.current = null;
      setSheets(parsed);
      setSheetIndex(initialSheet);
      const suggestedHeaderRow = suggestHeaderRowIndex(parsed[initialSheet].cells);
      setHeaderRowIndex(suggestedHeaderRow);
      setHeaderRowCount(suggestHeaderRowCount(parsed[initialSheet].cells, suggestedHeaderRow));
      setStep('sheet');
    } catch (err) {
      // 서버 검증 실패(형식·용량)는 여기로 온다 — 메시지를 그대로 보여 준다.
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message ?? (err as Error).message;
      failFileStep(message || '엑셀을 읽는 중 오류가 발생했습니다.');
    } finally {
      setParsing(false);
    }
  }, [file, sheets.length, failFileStep]);

  // 시트·헤더 행이 정해진 뒤에 ③단계 초안을 만든다(자동 매칭·칼럼 추론은 헤더 행에 달려
  // 있으므로 이 전이 시점에 계산해야 한다 — 매 렌더 재계산하면 사용자 수정이 되돌아간다).
  const handleEnterColumns = () => {
    // 근거(시트·헤더 줄·헤더 줄 수)가 그대로면 이미 만든 초안을 쓴다 — "이전"으로 돌아갔다
    // 오는 것만으로 사용자가 고친 매핑·칼럼 이름이 자동 추천으로 덮이면 안 된다.
    const basis = `${sheetIndex}:${headerRowIndex}:${headerRowCount}`;
    if (columnsBasisRef.current === basis) {
      setStep('columns');
      return;
    }
    columnsBasisRef.current = basis;
    if (props.mode === 'map') {
      const detectedPairs = detectExclusivePairs(cells, headerRowIndex, headerRowCount);
      setPairs(detectedPairs);
      setMapping(autoMatchColumns(targetFields, headerLabels, detectedPairs));
      setDefaults({});
    } else {
      const nextDrafts = buildComposeColumnDrafts({
        cells,
        headerRowIndex,
        headerRowCount,
        nextKey: props.nextFieldKey,
      });
      setDrafts(nextDrafts);
      setComposeLabelBaseline(Object.fromEntries(nextDrafts.map((d) => [d.colIndex, d.label])));
    }
    setStep('columns');
  };

  // 미저장 닫기 보호 — 사용자 결정(2026-10-31): 파일·시트·매핑 선택은 다시 고르면 되므로 세지 않고,
  // **칼럼 단계에서 손으로 적은 값**이 있을 때만 dirty다. map은 연결 안 한 칼럼의 기본값(비어 있지 않음),
  // compose는 칼럼 이름 — 자동 이름(초안 생성 시 이름·쌍 합침 토글이 바꿔 넣는 두 이름)과 다를 때.
  const isDirty =
    mode === 'map'
      ? Object.values(defaults).some((v) => v !== '')
      : drafts.some(
          (d) =>
            d.label !== composeLabelBaseline[d.colIndex] &&
            d.label !== d.pair?.mergedLabel &&
            d.label !== d.pair?.firstSoloLabel,
        );

  const handleSheetChange = (index: number) => {
    setSheetIndex(index);
    const nextCells = sheets[index]?.cells ?? [];
    const suggestedHeaderRow = suggestHeaderRowIndex(nextCells);
    setHeaderRowIndex(suggestedHeaderRow);
    setHeaderRowCount(suggestHeaderRowCount(nextCells, suggestedHeaderRow));
  };

  // 헤더 시작 줄이 바뀌면 줄 수도 그 줄 기준으로 다시 제안한다 — 직전 줄에서 고른 줄 수를
  // 새 줄에 그대로 끌고 가면 데이터 첫 줄이 조용히 헤더로 먹힌다.
  const handleHeaderRowChange = (rowIndex: number) => {
    setHeaderRowIndex(rowIndex);
    setHeaderRowCount(suggestHeaderRowCount(cells, rowIndex));
  };

  /** 헤더 줄 수로 고를 수 있는 상한 — 격자 남은 줄을 넘겨 봐야 데이터가 0줄이 될 뿐이다. */
  const maxHeaderRowCount = Math.min(
    MAX_HEADER_ROW_COUNT,
    Math.max(1, cells.length - headerRowIndex),
  );

  // 헤더 구간의 마지막 줄(0-based) — 시작 줄만 라디오로 잡히지만 사용자가 고른 것은 구간이다.
  const headerSpanEnd = headerRowIndex + headerRowCount - 1;
  /** 헤더 구간 아래로 남는 줄 — 0이면 데이터가 한 줄도 안 남는다는 뜻이라 그대로 알린다. */
  const dataRowCount = Math.max(0, cells.length - (headerSpanEnd + 1));
  const headerSpanSummary =
    dataRowCount === 0
      ? `${headerRowIndex + 1}~${headerSpanEnd + 1}번째 줄이 헤더 — 데이터로 읽을 줄이 없습니다`
      : headerRowCount > 1
        ? `헤더 구간 ${headerRowIndex + 1}~${headerSpanEnd + 1}번째 줄 · 데이터는 ${headerSpanEnd + 2}번째 줄부터 ${dataRowCount}줄`
        : `헤더 ${headerRowIndex + 1}번째 줄 · 데이터는 ${headerSpanEnd + 2}번째 줄부터 ${dataRowCount}줄`;

  const handleApply = () => {
    if (mode === 'map') {
      onApply({ mode: 'map', rows: built.rows, replace });
    } else {
      onApply({ mode: 'compose', fields: effectiveFields, rows: built.rows });
    }
  };

  const title = mode === 'map' ? '엑셀에서 행 추가' : '엑셀에서 데이터 시작';
  const includedCount = mode === 'map' ? targetFields.length : effectiveFields.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        datasetLabel && mode === 'map' ? (
          <span>
            {title} <span className="text-sm font-normal text-gray-400">— {datasetLabel}</span>
          </span>
        ) : (
          title
        )
      }
      ariaLabel={title}
      size="xl"
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} />
          {step !== 'file' && (
            <Button
              variant="secondary"
              onClick={() =>
                setStep(step === 'preview' ? 'columns' : step === 'columns' ? 'sheet' : 'file')
              }
            >
              이전
            </Button>
          )}
          {step === 'file' && (
            <Button variant="primary" onClick={() => void handleParse()} disabled={!file || parsing}>
              {parsing ? '읽는 중…' : '다음'}
            </Button>
          )}
          {step === 'sheet' && (
            <Button variant="primary" onClick={handleEnterColumns} disabled={cells.length === 0}>
              다음
            </Button>
          )}
          {step === 'columns' && (
            <Button
              variant="primary"
              onClick={() => setStep('preview')}
              disabled={includedCount === 0}
            >
              미리보기
            </Button>
          )}
          {step === 'preview' && (
            <Button
              variant={replace ? 'danger' : 'primary'}
              onClick={handleApply}
              disabled={built.rows.length === 0}
            >
              {mode === 'compose'
                ? `${built.rows.length}행으로 데이터 만들기`
                : replace
                  ? `기존 행 지우고 ${built.rows.length}행 넣기`
                  : `${built.rows.length}행 추가`}
            </Button>
          )}
        </>
      }
    >
      {error ? (
        <p className="mb-3 rounded bg-red-50 px-3 py-2 text-sm leading-relaxed text-red-700">
          {error}
        </p>
      ) : null}

      {/* ① 파일 선택 */}
      {step === 'file' ? (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-gray-600">
            {mode === 'map' ? (
              <>
                엑셀의 각 줄을 이 데이터셋의 <b>행</b>으로 가져옵니다. 칼럼 구성은 바뀌지 않고,
                어느 엑셀 칸을 어느 칼럼에 넣을지만 다음 단계에서 고릅니다.
              </>
            ) : (
              <>
                엑셀의 <b>헤더 줄이 칼럼</b>이 되고 나머지 줄이 행이 됩니다. 가져올 칼럼과 입력
                방식은 다음 단계에서 고르고 고칠 수 있습니다.
              </>
            )}
          </p>
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="dataset-xlsx-file-input"
            >
              엑셀 파일
            </label>
            <input
              id="dataset-xlsx-file-input"
              ref={fileInputRef}
              type="file"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
              onChange={handleSelectFile}
              className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-1 file:text-sm file:text-gray-700 hover:file:bg-gray-200"
            />
          </div>
          <ul className="space-y-1 rounded bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-500">
            <li>· 엑셀(.xlsx)만 올릴 수 있습니다 (최대 5MB).</li>
            <li>· 구형 엑셀(.xls)은 xlsx로 저장 후 올려주세요.</li>
            <li>· 시트가 여러 개면 다음 단계에서 고릅니다. 한 번에 한 시트를 가져옵니다.</li>
            <li>· 셀 값은 화면에 보이는 문자열 그대로 읽습니다(수식은 계산된 결과).</li>
          </ul>
        </div>
      ) : null}

      {/* ② 시트·헤더 행 */}
      {step === 'sheet' ? (
        <div className="space-y-3">
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="dataset-xlsx-sheet-select"
            >
              시트
            </label>
            <select
              id="dataset-xlsx-sheet-select"
              value={sheetIndex}
              onChange={(e) => handleSheetChange(Number(e.target.value))}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
            >
              {sheets.map((s, i) => (
                <option key={`${s.name}-${i}`} value={i}>
                  {s.name} ({s.rowCount}줄 × {s.colCount}칸)
                </option>
              ))}
            </select>
          </div>

          {activeSheet && (activeSheet.truncatedRows || activeSheet.truncatedCols) ? (
            <p className="rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
              이 시트는 {activeSheet.truncatedRows ? '1000줄' : ''}
              {activeSheet.truncatedRows && activeSheet.truncatedCols ? '·' : ''}
              {activeSheet.truncatedCols ? '100칸' : ''}까지만 읽었습니다. 넘는 부분은 가져오지
              않습니다.
            </p>
          ) : null}

          <div>
            <p className="mb-1 text-sm font-medium text-gray-700">
              헤더 구간 — 칼럼 이름이 <b>시작</b>되는 줄을 고르세요
            </p>
            <p className="mb-2 text-xs leading-relaxed text-gray-500">
              고른 줄부터 <b>헤더 줄 수</b>만큼이 헤더 구간이고, 그 <b>아래</b>부터 데이터로
              읽습니다. 구간 안의 줄은 이미 헤더라 눌러도 시작이 바뀌지 않습니다 — 구간 <b>밖</b>{' '}
              줄을 누르면 그 줄이 새 시작이 됩니다. 줄 번호는 <b>읽어 온 표</b> 기준이라 엑셀
              화면의 행 번호와 다를 수 있습니다(빈 위쪽 행은 세지 않습니다).
            </p>

            {cells.length > 0 ? (
              <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                <label
                  className="text-sm font-medium text-gray-700"
                  htmlFor="dataset-xlsx-header-row-count"
                >
                  헤더 줄 수
                </label>
                <select
                  id="dataset-xlsx-header-row-count"
                  value={headerRowCount}
                  onChange={(e) => setHeaderRowCount(Number(e.target.value))}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm"
                >
                  {Array.from({ length: maxHeaderRowCount }, (_, i) => i + 1).map((count) => (
                    <option key={count} value={count}>
                      {count}줄
                    </option>
                  ))}
                </select>
                <span
                  className={
                    dataRowCount === 0
                      ? 'text-xs font-medium text-amber-700'
                      : 'text-xs text-brand-700'
                  }
                >
                  {headerSpanSummary}
                </span>
              </div>
            ) : null}

            {cells.length === 0 ? (
              <p className="text-sm text-gray-400">이 시트는 비어 있습니다.</p>
            ) : (
              <div className="max-h-64 overflow-auto rounded border border-gray-200">
                <table className="w-full border-collapse text-xs">
                  <tbody>
                    {cells.slice(0, 20).map((row, ri) => {
                      // 고른 것은 줄 하나가 아니라 구간이다 — 구간에 든 줄은 전부 선택된 것처럼
                      // 보여야 한다(라디오 checked는 name이 하나라 시작 줄에만 걸린다).
                      const inHeaderSpan = ri >= headerRowIndex && ri <= headerSpanEnd;
                      return (
                        <tr
                          key={ri}
                          className={inHeaderSpan ? 'bg-brand-50' : 'hover:bg-gray-50'}
                        >
                          <td
                            className={`w-24 border-b border-gray-100 px-2 py-1 align-top ${
                              inHeaderSpan ? 'border-l-2 border-l-brand-500' : ''
                            }`}
                          >
                            <label
                              className={`flex items-center gap-1.5 whitespace-nowrap ${
                                inHeaderSpan
                                  ? 'cursor-default text-brand-800'
                                  : 'cursor-pointer text-gray-500'
                              }`}
                            >
                              <input
                                type="radio"
                                name="dataset-xlsx-header-row"
                                checked={ri === headerRowIndex}
                                // 구간 안은 이미 헤더다 — 눌러도 시작을 옮기지 않는다(2번째 줄을
                                // 눌러 헤더가 한 줄 밀리면 데이터 첫 줄이 조용히 먹힌다).
                                // 클릭 자체를 취소해 라디오가 켜졌다 되돌아가는 깜빡임을 없앤다
                                // (상태가 안 바뀌면 React 복원에 기대야 하는데, 그건 한 프레임
                                // 켜진 뒤 되돌아간다). onChange 가드는 그 뒤를 받친다.
                                onClick={(e) => {
                                  if (inHeaderSpan) e.preventDefault();
                                }}
                                onChange={() => {
                                  if (!inHeaderSpan) handleHeaderRowChange(ri);
                                }}
                              />
                              {ri + 1}번째 줄
                            </label>
                            {/* 배지는 label 밖에 둔다 — 라디오의 접근 이름을 "N번째 줄"로 유지해야
                                구간 크기가 바뀌어도 같은 줄을 같은 이름으로 고를 수 있다. */}
                            {inHeaderSpan ? (
                              <span className="mt-0.5 inline-block rounded bg-brand-100 px-1 py-px text-[10px] font-medium text-brand-700">
                                {headerRowCount > 1
                                  ? `헤더 ${ri - headerRowIndex + 1}/${headerRowCount}`
                                  : '헤더'}
                              </span>
                            ) : null}
                          </td>
                          {row.slice(0, 12).map((cell, ci) => (
                            <td
                              key={ci}
                              className="max-w-[10rem] truncate border-b border-l border-gray-100 px-2 py-1 text-gray-700"
                              title={cell}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {cells.length > 20 ? (
              <p className="mt-1 text-xs text-gray-400">
                앞 20줄만 보입니다 (전체 {cells.length}줄).
              </p>
            ) : null}
            <p className="mt-2 text-xs leading-relaxed text-gray-500">
              헤더가 두 줄에 걸치면(예: 병합된 「점검결과」 아래 양호·불량) <b>헤더 줄 수</b>를
              올리세요 — 구간이 그만큼 늘고, 칼럼 이름은 위아래 문구를 합쳐 만듭니다. 병합된 칸의
              값은 병합 범위 전체에 채워 보입니다.
            </p>
          </div>
        </div>
      ) : null}

      {/* ③-map 칼럼 매핑 */}
      {step === 'columns' && mode === 'map' ? (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-gray-600">
            데이터셋 칼럼마다 가져올 엑셀 칸을 고르세요. 이름이 같은 칸은 미리 연결해 두었습니다.
            연결하지 않은 칼럼은 <b>모든 행에 같은 값</b>을 넣습니다(비워 두면 빈 값).
          </p>
          {pairs.length > 0 ? (
            <p className="rounded bg-brand-50 px-3 py-2 text-xs leading-relaxed text-brand-700">
              한쪽에만 표시되는 두 칸({pairs.map((p) => `${p.topLabel}: ${p.subLabels[0]}/${p.subLabels[1]}`).join(' · ')})은
              목록 끝의 <b>합침</b> 항목으로 한 칼럼에 넣을 수 있습니다 — 체크박스 칼럼이면 첫
              칸 표시가 체크로 저장됩니다.
            </p>
          ) : null}
          {targetFields.length === 0 ? (
            <p className="text-sm text-gray-400">
              이 데이터셋에는 칼럼이 없습니다 — 먼저 칼럼을 추가하세요.
            </p>
          ) : (
            <div className="space-y-1.5">
              {targetFields.map((f) => {
                const col = mapping[f.key];
                const unmapped = col === null || col === undefined;
                return (
                  <div key={f.key} className="flex items-center gap-2">
                    <span
                      className="w-32 shrink-0 truncate text-xs text-gray-700"
                      title={`${f.label || f.key} (${f.key})`}
                    >
                      {f.label || f.key}
                    </span>
                    <select
                      aria-label={`${f.label || f.key} 칼럼에 넣을 엑셀 칸`}
                      value={sourceToSelectValue(col)}
                      onChange={(e) => {
                        const selected = e.target.value;
                        const matchedPair = pairs.find(
                          (p) => pairOptionValue(p.colIndexes) === selected,
                        );
                        const nextSource: ColumnSource =
                          selected === ''
                            ? null
                            : matchedPair
                              ? { pair: matchedPair.colIndexes, subLabels: matchedPair.subLabels }
                              : Number(selected);
                        setMapping((prev) => ({ ...prev, [f.key]: nextSource }));
                      }}
                      className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-1.5 py-1 text-xs"
                    >
                      <option value="">— 엑셀에서 안 가져옴 —</option>
                      {headerLabels.map((_, ci) => (
                        <option key={ci} value={ci}>
                          {excelColumnLabel(headerLabels, ci)}
                        </option>
                      ))}
                      {pairs.map((p) => (
                        <option key={pairOptionValue(p.colIndexes)} value={pairOptionValue(p.colIndexes)}>
                          {p.topLabel} ({p.subLabels[0]}/{p.subLabels[1]} 합침)
                        </option>
                      ))}
                    </select>
                    {unmapped ? (
                      <DefaultValueControl
                        field={f}
                        value={defaults[f.key] ?? ''}
                        onChange={(v) => setDefaults((prev) => ({ ...prev, [f.key]: v }))}
                      />
                    ) : (
                      <span className="w-32 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-gray-400">
            연결된 칼럼 {mappedColumnCount(targetFields, mapping)} / {targetFields.length}
          </p>
        </div>
      ) : null}

      {/* ③-compose 칼럼 구성 */}
      {step === 'columns' && mode === 'compose' ? (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-gray-600">
            가져올 칼럼을 고르세요. 입력 방식은 값을 보고 <b>추천</b>한 것이라 그대로 두거나 바꿔도
            됩니다.
          </p>
          <div className="space-y-1.5">
            {drafts.map((d, di) =>
              // 병합 중인 쌍의 둘째 열은 그리지 않는다 — di는 원본 배열 index라 어긋나지 않는다.
              hiddenPairCols.has(d.colIndex) ? null : (
                <div key={d.colIndex} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="flex w-6 shrink-0 items-center justify-center">
                      <input
                        type="checkbox"
                        aria-label={`${d.label} 칼럼 가져오기`}
                        checked={d.include}
                        onChange={(e) =>
                          setDrafts((prev) =>
                            prev.map((x, xi) =>
                              xi === di ? { ...x, include: e.target.checked } : x,
                            ),
                          )
                        }
                      />
                    </label>
                    <input
                      type="text"
                      aria-label={`${d.label} 칼럼 이름`}
                      value={d.label}
                      disabled={!d.include}
                      onChange={(e) =>
                        setDrafts((prev) =>
                          prev.map((x, xi) => (xi === di ? { ...x, label: e.target.value } : x)),
                        )
                      }
                      className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-400"
                    />
                    <select
                      aria-label={`${d.label} 입력 방식`}
                      value={d.widget}
                      disabled={!d.include}
                      onChange={(e) =>
                        setDrafts((prev) =>
                          prev.map((x, xi) =>
                            xi === di ? { ...x, widget: e.target.value as InferredWidget } : x,
                          ),
                        )
                      }
                      className="w-24 shrink-0 rounded border border-gray-300 bg-white px-1.5 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      {(Object.keys(WIDGET_LABEL) as InferredWidget[]).map((w) => (
                        <option key={w} value={w}>
                          {WIDGET_LABEL[w]}
                        </option>
                      ))}
                    </select>
                    <span className="w-16 shrink-0 truncate font-mono text-[11px] text-gray-400">
                      {d.key}
                    </span>
                  </div>
                  {d.pair ? (
                    <label className="ml-8 flex items-center gap-1.5 text-[11px] leading-relaxed text-gray-500">
                      <input
                        type="checkbox"
                        aria-label={`${d.pair.mergedLabel} — ${d.pair.subLabels[0]}·${d.pair.subLabels[1]} 두 칸 합치기`}
                        checked={d.pair.merged}
                        disabled={!d.include}
                        onChange={() => setDrafts((prev) => togglePairMerge(prev, di))}
                      />
                      <span>
                        「{d.pair.subLabels[0]}」·「{d.pair.subLabels[1]}」 두 칸 합침 —{' '}
                        {d.pair.merged ? (
                          <>
                            <b>{d.pair.subLabels[0]}</b> 표시가 체크(해제하면 개별 2칼럼)
                          </>
                        ) : (
                          '체크하면 1칼럼으로 합칩니다'
                        )}
                      </span>
                    </label>
                  ) : null}
                </div>
              ),
            )}
          </div>
          <p className="text-xs text-gray-400">
            가져올 칼럼 {effectiveFields.length} / {drafts.length - hiddenPairCols.size}
          </p>
        </div>
      ) : null}

      {/* ④ 미리보기 + ⑤ 반영 방식 — 카탈로그 모달과 공용(datasetImportShared). */}
      {step === 'preview' ? (
        <ImportPreviewStep
          fields={effectiveFields}
          rows={built.rows}
          warnings={built.warnings}
          defaultWarnings={built.defaultWarnings}
          summary={
            <>
              읽은 행 <b>{built.rows.length}개</b>
              {built.skippedEmptyRows > 0 ? (
                <span className="text-gray-400"> · 빈 줄 {built.skippedEmptyRows}개 건너뜀</span>
              ) : null}
            </>
          }
          emptyMessage="가져올 행이 없습니다. 헤더 줄이나 칼럼 연결을 다시 확인해주세요."
          warningRowLabel={(n) => `표에서 ${n}번째 줄`}
          applyMode={
            mode === 'map'
              ? {
                  radioName: 'dataset-xlsx-apply-mode',
                  existingRowCount,
                  replace,
                  onReplaceChange: setReplace,
                }
              : undefined
          }
        />
      ) : null}
    </Modal>
  );
}
